import { type CSSProperties, useMemo } from 'react'
import { useBackgroundPlacementOptional } from '@/contexts/background-placement-context'
import { resolveBackgroundOptionsLayout } from '@/lib/backgrounds/background-options-layout'
import type { SpreadGeometryInput } from '@/lib/backgrounds/spread-page-rects'
import { useSpreadPageRects } from '../hooks/use-spread-page-rects'
import { BackgroundOptionsToolbar } from './components/background-options-toolbar'
import { useElementWidth } from './hooks/use-element-width'

/** Screen px between the bottom of the toolbar row and the spread's bleed-outer top edge. */
const TOOLBAR_GAP_PX = 12
/** Screen px between each page toolbar and the spine (so the two never touch). */
const SPINE_GAP_PX = 4

type SpreadBackgroundOptionsLayerProps = SpreadGeometryInput & {
  /** Canvas zoom — this layer draws in screen px (`screen = canvasPt * scale`) so the toolbars
   *  keep one on-screen size at every zoom, like the Visual layer's selection chrome. */
  scale: number
}

/**
 * Tint + Remove toolbars for the current spread's placed backgrounds, sitting just above the
 * spread: one for a spread background, otherwise one per page that has a background, each over
 * its own page's outer edge (`resolveBackgroundOptionsLayout`). Only shown while the Backgrounds
 * panel is open, and hidden while a background is being dragged or placed.
 *
 * Mount as a sibling of the canvas's `scale()` wrapper (like the Visual layer), not inside it.
 */
export function SpreadBackgroundOptionsLayer({
  scale,
  bleedWidth,
  pageTrimWidth,
  trimHeight,
  bleedMargin,
  spreadOriginCanvasX,
  singlePageSide,
}: SpreadBackgroundOptionsLayerProps) {
  const placement = useBackgroundPlacementOptional()
  const pageRects = useSpreadPageRects({
    bleedWidth,
    pageTrimWidth,
    trimHeight,
    bleedMargin,
    spreadOriginCanvasX,
    singlePageSide,
  })

  const placedBackgrounds = placement?.placedBackgrounds
  // The right page toolbar's rendered width (screen px) — it decides whether that toolbar fits at
  // the spread's outer right edge or has to start at the spine. Its contents are fixed screen-px
  // sizes, so zoom doesn't change it; it's measured on mount and on actual resizes only.
  const [rightCellRef, rightToolbarWidth] = useElementWidth<HTMLDivElement>()
  const layout = useMemo(
    () =>
      placedBackgrounds
        ? resolveBackgroundOptionsLayout(placedBackgrounds, pageRects, bleedMargin)
        : null,
    [placedBackgrounds, pageRects, bleedMargin]
  )

  if (
    !placement ||
    !layout ||
    !placement.isBackgroundsPanelOpen ||
    placement.isDraggingBackground
  ) {
    return null
  }

  const { anchor, spineX, items } = layout

  // Screen px from the row's left edge to the spine.
  const spineOffset = (spineX - anchor.x) * scale
  // Does the right page toolbar fit between the spine (plus its gap) and the outer right edge?
  const rightFitsAtEnd = rightToolbarWidth <= anchor.width * scale - spineOffset - SPINE_GAP_PX

  // The row is a zero-height line just above the spread; each toolbar sits on it (`bottom: 0`) at
  // its own spot, so there's no shared flow to wrap. The left toolbar ends at the spine; the right
  // one ends at the outer right edge while it fits there, and otherwise starts at the spine — so
  // the two never overlap and stay on one line at every zoom (at a low zoom they may reach past
  // the spread's outer edges rather than stack).
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: anchor.x * scale,
        top: anchor.y * scale - TOOLBAR_GAP_PX,
        width: anchor.width * scale,
        height: 0,
      }}
      data-background-options-layer=""
    >
      {items.map((item) => {
        const toolbar = (
          <BackgroundOptionsToolbar
            item={item}
            tint={placedBackgrounds?.[item.slot]?.tint ?? 100}
            onTintChange={placement.updateBackgroundTint}
            onRemove={placement.removeBackground}
          />
        )

        const position: CSSProperties =
          item.align === 'endAtSpine'
            ? // Right edge just left of the spine.
              { left: spineOffset - SPINE_GAP_PX, transform: 'translateX(-100%)' }
            : item.align === 'endOrSpine' && !rightFitsAtEnd
              ? // No room at the outer edge: left edge just right of the spine.
                { left: spineOffset + SPINE_GAP_PX }
              : // Right edge at the spread's outer right edge.
                { right: 0 }

        return (
          <div
            key={item.slot}
            ref={item.align === 'endOrSpine' ? rightCellRef : undefined}
            // `w-max`: the toolbar's own width wherever it sits, so the measurement doesn't depend
            // on the position it decides.
            className="absolute bottom-0 flex w-max whitespace-nowrap"
            style={position}
            data-background-options-cell={item.slot}
          >
            {toolbar}
          </div>
        )
      })}
    </div>
  )
}
